import { nanoid } from '@reduxjs/toolkit'
import { catchError, concat, defer, filter, map, mergeMap, of, withLatestFrom } from 'rxjs'
import type { Epic } from '../../index.js'
import { spinMetaKey, spinMetaSlice } from '../../Slice/Spin/SpinMetaSlice.ts'
import { authenticationExpiredOr } from '../../../Framework/Epic/AuthenticationExpired.js'
// Imported directly, as in SpinStatusEpics: SpinMeta is Promise-based and not in the Repository barrel.
import { fetchSpinUnspindCount } from '../../../Repository/Visualization/SpinMeta.js'

const { requestSpinMeta, requestSpinMetaStarted, requestSpinMetaSuccess, requestSpinMetaFailure } = spinMetaSlice.actions

/** Fetch-if-needed, as fetchSpinStateEpic: a `loading` or `ready` key is skipped, `idle` and `error` fetch; nothing while the session is expired. */
export const fetchSpinMetaEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(requestSpinMeta.match),
    withLatestFrom(state$),
    filter(([action, state]) => {
      const status = state.spinMeta.entries[spinMetaKey(action.payload.query)]?.status
      return !state.authentication.sessionExpired && status !== 'loading' && status !== 'ready'
    }),
    mergeMap(([action]) => {
      const { query } = action.payload
      const key = spinMetaKey(query)
      const requestId = nanoid()
      return concat(
        of(requestSpinMetaStarted({ key, scopeId: query.scopeId, requestId })),
        defer(() => fetchSpinUnspindCount(query.scopeId, query.algorithmType, query.atTime)).pipe(
          map((data) => requestSpinMetaSuccess({ key, requestId, data })),
          catchError((error: { status?: number }) =>
            authenticationExpiredOr({
              error,
              failedRequest: action.type,
              failureAction: of(requestSpinMetaFailure({ key, requestId, error }))
            })
          )
        )
      )
    })
  )
